import { Link, LinkOff } from "@mui/icons-material";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { ObservationMediaDisplay } from "~/modules/mapEditor/components/ObservationMediaDisplay";
import { useMapEditorFeatureHover } from "~/modules/mapEditor/hooks/useMapEditorFeatureHover";

import { MapEditorObservationPanelEcologicalFunctionFragment } from "./fragments.generated";

interface EcologicalFunctionCardBaseProps {
    ecologicalFunction: MapEditorObservationPanelEcologicalFunctionFragment;
}

interface EcologicalFunctionCardLinkedProps extends EcologicalFunctionCardBaseProps {
    isLinked: true;
    onUnlinkClick?: (id: string) => void;
    onLinkClick?: never;
}

interface EcologicalFunctionCardLinkableProps extends EcologicalFunctionCardBaseProps {
    isLinked: false;
    onLinkClick: (id: string) => void;
    onUnlinkClick?: never;
}

export type EcologicalFunctionCardProps = EcologicalFunctionCardLinkedProps | EcologicalFunctionCardLinkableProps;

export function EcologicalFunctionCard({
    ecologicalFunction,
    onLinkClick,
    onUnlinkClick,
    isLinked,
}: EcologicalFunctionCardProps) {
    const { t } = useTranslation("mapEditor");
    const hovering = useMapEditorFeatureHover();

    return (
        <Stack
            onMouseEnter={() => hovering.set(ecologicalFunction.featureId)}
            onMouseLeave={() => hovering.clear()}
            sx={theme => ({
                padding: 2,
                border: `1px solid ${theme.palette.divider}`,
                width: "100%",
            })}
        >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Stack>
                    <Typography>{ecologicalFunction.type.name}</Typography>
                    <Typography variant="body2" color="textSecondary">
                        {ecologicalFunction.speciesList.map(speciesListItem => speciesListItem.name).join(", ")}
                    </Typography>
                </Stack>
                {(onUnlinkClick !== undefined || onLinkClick !== undefined) && (
                    <Box>
                        {isLinked ? (
                            <Button
                                startIcon={<LinkOff />}
                                variant="outlined"
                                color="error"
                                onClick={_ => onUnlinkClick?.(ecologicalFunction.id)}
                            >
                                {t("ecologicalFunctionCard.btnUnlink")}
                            </Button>
                        ) : (
                            <Button startIcon={<Link />} variant="outlined" onClick={_ => onLinkClick?.(ecologicalFunction.id)}>
                                {t("ecologicalFunctionCard.btnLink")}
                            </Button>
                        )}
                    </Box>
                )}
            </Stack>

            {ecologicalFunction.medias.length > 0 && (
                <Stack rowGap={1}>
                    <Typography variant="body2" color="textSecondary">
                        {t("ecologicalFunctionCard.labelMedia")}
                    </Typography>
                    <ObservationMediaDisplay media={ecologicalFunction.medias} />
                </Stack>
            )}
        </Stack>
    );
}
